import { IntegrationsDataList, IntegrationType, ModuleData, ModuleType, ProviderData } from '../Types';

export interface ApiResponseData {
    systemState: boolean;
    systemVersion: string;
    licenseHoursThreshold: number;
    licenseExpiresInHours: number;
    authentications: number;
    users: number;
    serviceAccounts: number;
    privilegedAccounts: number;
    dataSources: Array<{
        category: IntegrationType;
        name: string;
        displayName: string;
        active: boolean;
        isConnected?: boolean;
    }>;
    unifiedAccounts: number;
    criticalRiskUsers: number;
    incidents: number;
    resolvedIncidents: number;
    securityExposures: number;
    staleUsers: number;
    criticalExposureEntities: number;
    totalNhiAccounts: number;
    protectedNHIs: number;
    nhiAccountsAtRisk: number;
    denyPoliciesCount: number;
    totalBlocked: number;
    protectedDenyUsersCount: number;
    privilegedUsers: number;
    crossTierAuthentications: number;
    protectedPrivilegedUsers: number;
    mfaPoliciesCount: number;
    protectedMfaUsersCount: number;
    totalMfa: number;
    resources: number;
    applications: number;
    staleAccessUsers: number;
}

export interface SystemStatistics {
    users: number;
    serviceAccounts: number;
    totalPrivilegedAccounts: number;
    totalAuthentications: number;
}

export interface LicenceExpiration {
    threshold: number;
    expiresInHours: number | null;
}

export const mapSystemStatistics = (data: ApiResponseData): SystemStatistics => ({
    totalAuthentications: data.authentications || 0,
    users: data.users || 0,
    serviceAccounts: data.serviceAccounts || 0,
    totalPrivilegedAccounts: data.privilegedAccounts || 0
});

export const mapLicenceExpiration = (data: ApiResponseData): LicenceExpiration => ({
    threshold: data.licenseHoursThreshold,
    expiresInHours: data.licenseExpiresInHours
});

export const mapModulesData = (
    data: ApiResponseData,
    currentModules: Record<ModuleType, ModuleData>
): Record<ModuleType, ModuleData> => {
    const updatedModules = { ...currentModules };

    const identityInventory = updatedModules[ModuleType.identityInventory];
    identityInventory.metrics['identityPlatforms'].value = data.dataSources?.length || 0;
    identityInventory.metrics['authentications'].value = data.authentications || 0;
    identityInventory.metrics['unifiedAccounts'].value = data.unifiedAccounts || 0;

    const itdr = updatedModules[ModuleType.itdr];
    itdr.metrics['criticalRiskUsers'].value = data.criticalRiskUsers;
    itdr.metrics['incidents'].value = data.incidents;
    itdr.metrics['resolvedIncidents'].value = data.resolvedIncidents;

    const ispm = updatedModules[ModuleType.ispm];
    ispm.metrics['securityExposures'].value = data.securityExposures || 0;
    ispm.metrics['staleUsers'].value = data.staleUsers || 0;
    ispm.metrics['criticalExposureEntities'].value = data.criticalExposureEntities || 0;

    const nhiVisibility = updatedModules[ModuleType.nhiVisibility];
    nhiVisibility.metrics['nhis'].value = data.totalNhiAccounts || 0;
    nhiVisibility.metrics['protectedNHIs'].value = data.protectedNHIs || 0;
    nhiVisibility.metrics['nhisAtRisk'].value = data.nhiAccountsAtRisk || 0;

    const authFirewall = updatedModules[ModuleType.authenticationFirewall];
    authFirewall.metrics['activePolicies'].value = data.denyPoliciesCount || 0;
    authFirewall.metrics['deniedAuthentications'].value = data.totalBlocked;
    authFirewall.metrics['protectedUsers'].value = data.protectedDenyUsersCount;

    const privilegedAccess = updatedModules[ModuleType.privilegedAccessSecurity];
    privilegedAccess.metrics['privilegedAccounts'].value = data.privilegedUsers || 0;
    privilegedAccess.metrics['crossTierAuthentications'].value = data.crossTierAuthentications || 0;
    privilegedAccess.metrics['protectedPrivilegedAccounts'].value = data.protectedPrivilegedUsers || 0;

    const mfaUsers = updatedModules[ModuleType.mfaUsers];
    mfaUsers.metrics['mfaPolicies'].value = data.mfaPoliciesCount || 0;
    mfaUsers.metrics['protectedUsers'].value = data.protectedMfaUsersCount;
    mfaUsers.metrics['mfaRequests'].value = data.totalMfa;

    const accessAnalysis = updatedModules[ModuleType.accessAnalysis];
    accessAnalysis.metrics['resources'].value = data.resources || 0;
    accessAnalysis.metrics['applications'].value = data.applications || 0;
    accessAnalysis.metrics['staleAccess'].value = data.staleAccessUsers || 0;

    return updatedModules;
};

export const mapIntegrationsData = (
    data: ApiResponseData,
    currentIntegrations: IntegrationsDataList
): IntegrationsDataList => {
    const updatedIntegrations = { ...currentIntegrations };

    if (data.dataSources) {
        data.dataSources.forEach((dataSource) => {
            const categoryIntegration = updatedIntegrations[dataSource.category];
            if (categoryIntegration) {
                const existingProvider = categoryIntegration.providers[dataSource.name];
                if (!existingProvider?.active) {
                    categoryIntegration.providers[dataSource.name] = dataSource;
                }
            }
        });
    }

    return updatedIntegrations;
};
